import { cn } from "../../lib/utils";

interface EarningsSkeletonProps {
  className?: string;
}

export function EarningsSummarySkeleton({ className }: EarningsSkeletonProps) {
  return (
    <div className={cn("rounded-lg border bg-white p-4 space-y-3", className)}>
      <div className="h-4 bg-gray-200 rounded animate-pulse w-24" />
      <div className="h-6 bg-gray-200 rounded animate-pulse w-32" />
      <div className="h-3 bg-gray-200 rounded animate-pulse w-16" />
    </div>
  );
}

export function PayoutDetailsSkeleton({ className }: EarningsSkeletonProps) {
  return (
    <div className={cn("rounded-lg border bg-white p-4 space-y-4", className)}>
      <div className="h-5 bg-gray-200 rounded animate-pulse w-32" />
      <div className="space-y-3">
        <div className="flex justify-between">
          <div className="h-4 bg-gray-200 rounded animate-pulse w-24" />
          <div className="h-4 bg-gray-200 rounded animate-pulse w-16" />
        </div>
        <div className="flex justify-between">
          <div className="h-4 bg-gray-200 rounded animate-pulse w-20" />
          <div className="h-4 bg-gray-200 rounded animate-pulse w-24" />
        </div>
        <div className="flex justify-between">
          <div className="h-4 bg-gray-200 rounded animate-pulse w-28" />
          <div className="h-4 bg-gray-200 rounded animate-pulse w-20" />
        </div>
      </div>
    </div>
  );
}

export function EarningsPeriodSkeleton({ className }: EarningsSkeletonProps) {
  return (
    <div className={cn("rounded-lg border bg-white p-4 space-y-3", className)}>
      <div className="flex justify-between items-start">
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded animate-pulse w-20" />
          <div className="h-3 bg-gray-200 rounded animate-pulse w-32" />
        </div>
        <div className="h-5 bg-gray-200 rounded animate-pulse w-16" />
      </div>
      <div className="flex gap-2">
        <div className="h-6 bg-gray-200 rounded-full animate-pulse w-24" />
        <div className="h-6 bg-gray-200 rounded-full animate-pulse w-20" />
      </div>
    </div>
  );
}

export function EarningsPageSkeleton({ isWebWorkspace = false }: { isWebWorkspace?: boolean } = {}) {
  const header = isWebWorkspace ? (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
      <div className="space-y-2">
        <div className="h-3 bg-gray-200 rounded animate-pulse w-24" />
        <div className="h-6 bg-gray-200 rounded animate-pulse w-32" />
        <div className="h-4 bg-gray-200 rounded animate-pulse w-64" />
      </div>
      <div className="flex items-center gap-3">
        <div className="h-10 bg-gray-200 rounded-xl animate-pulse w-28" />
        <div className="h-10 bg-gray-200 rounded-xl animate-pulse w-32" />
      </div>
    </div>
  ) : (
    <div className="flex items-center gap-2">
      <div className="h-8 w-8 bg-gray-200 rounded-full animate-pulse" />
      <div className="h-5 bg-gray-200 rounded animate-pulse w-20" />
    </div>
  );

  const mainContent = (
    <div className={cn("grid gap-6", isWebWorkspace ? "grid-cols-1 lg:grid-cols-[2fr_1fr]" : "grid-cols-1")}>
      {/* Column 1: metrics & card */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <EarningsSummarySkeleton />
          <EarningsSummarySkeleton />
        </div>
        <PayoutDetailsSkeleton />
        <div className="h-24 bg-gray-200 rounded-2xl animate-pulse w-full" />
      </div>

      {/* Column 2: Period history */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-5 bg-gray-200 rounded animate-pulse w-24" />
          <div className="flex gap-2">
            <div className="h-7 bg-gray-200 rounded animate-pulse w-12" />
            <div className="h-7 bg-gray-200 rounded animate-pulse w-14" />
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <EarningsPeriodSkeleton />
          <EarningsPeriodSkeleton />
          <EarningsPeriodSkeleton />
        </div>
      </div>
    </div>
  );

  return (
    <div className={isWebWorkspace ? "w-full space-y-6" : "px-4 pt-4 pb-32 space-y-6"}>
      {header}
      {mainContent}
    </div>
  );
}